import { useState } from 'react';
import gupshupLogo from '../../assets/gupshup-icon.png';

const SETUP_GUIDE_URL = 'https://docs.gupshup.io/docs/bloomreach-integration';

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const ExternalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

// Shown when the widget URL is opened outside Bloomreach (no app_hello from a Bloomreach
// origin). Nothing else renders and no API is called; this only points the user back in.
const OutsideBloomreach = () => {
  const [copied, setCopied] = useState(false);
  const widgetUrl = window.location.href;

  // Split into origin (never breaks) and the rest (path/search/hash) so the code box can
  // wrap at most once, right before the path, instead of breaking mid-host on a hyphen.
  let urlOrigin = null;
  let urlRest = null;
  try {
    const parsedUrl = new URL(widgetUrl);
    urlOrigin = parsedUrl.origin;
    urlRest = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    // widgetUrl isn't a parseable URL; the code box renders the plain string instead.
  }

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(widgetUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (http, permissions); the URL stays selectable.
    }
  };

  return (
    <div className="bloomreach-widget br-shell br-outside" data-testid="bloomreach-widget-outside">
      <div className="br-outside__card">
        <div className="br-outside__header">
          <span className="br-outside__logo-wrap">
            <img className="br-outside__logo" src={gupshupLogo} alt="Gupshup" />
          </span>
          <div>
            <h2 className="br-outside__title">Gupshup WhatsApp</h2>
            <span className="br-outside__badge">for Bloomreach</span>
          </div>
        </div>

        <div className="br-outside__notice" role="status">
          <InfoIcon />
          <span>
            This app runs inside Bloomreach. Open it from a scenario to set up and send WhatsApp
            campaigns.
          </span>
        </div>

        <h3 className="br-outside__section">Set it up in 3 steps</h3>
        <ol className="br-outside__steps">
          <li>
            <span className="br-outside__step-no">1</span>
            <div>
              In Bloomreach, go to <strong>Data &amp; Assets → Integrations</strong> and create a{' '}
              <strong>Webhook preset</strong> of type <strong>Widget Webhook</strong>, named{' '}
              <strong>Gupshup WhatsApp</strong>.
            </div>
          </li>
          <li>
            <span className="br-outside__step-no">2</span>
            <div>
              Paste this URL as the preset's <strong>Widget URL</strong>.
              <div className="br-outside__url">
                <code data-testid="bloomreach-widget-outside-url">
                  {urlOrigin !== null ? (
                    <>
                      <span className="br-outside__url-segment">{urlOrigin}</span>
                      <wbr />
                      <span className="br-outside__url-segment">{urlRest}</span>
                    </>
                  ) : (
                    widgetUrl
                  )}
                </code>
                <button
                  type="button"
                  className={`br-outside__copy${copied ? ' br-outside__copy--done' : ''}`}
                  data-testid="bloomreach-widget-outside-copy"
                  onClick={copyUrl}
                  aria-live="polite"
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </li>
          <li>
            <span className="br-outside__step-no">3</span>
            <div>
              In a scenario, add the <strong>Gupshup WhatsApp</strong> webhook node and open it.
              This app loads right there.
            </div>
          </li>
        </ol>

        <a
          className="br-outside__guide"
          data-testid="bloomreach-widget-outside-guide"
          href={SETUP_GUIDE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          View full setup guide
          <ExternalIcon />
        </a>
      </div>

      <div className="br-outside__footer">
        <span>Powered by</span>
        <img className="br-shell__footer-logo" src={gupshupLogo} alt="Gupshup" />
      </div>
    </div>
  );
};

export default OutsideBloomreach;
